import React, { Fragment, useEffect, useState, useRef } from 'react'

import EloModal from 'ui/EloModal'
import { LoadingMask } from '@elo-kit/components/loading-mask/LoadingMask'

import { notify } from 'libs/common/notify'

import {
  injectStripeScript,
  createStripeClient,
  createStripeElements,
  createStripeP24,
  createStripeIdeal,
  isStripeInjected,
  handleP24Transaction,
  handleIdealTransaction,
  handleSofortTransaction,
  handleStripeCardTransaction,
  createStripeKlarna,
  createStripeCard,
  getKlarnaFormSettings,
  handleTransactionResponse,
  handleAdditionalConfirmation,
} from '@elo-kit/utils/stripe.utils'
import { snakeCaseToCamelCase } from '@elo-kit/utils/nameStyle.utils'
import { getPubKey } from 'utils/paymentSettingShared.utils'

import {
  IDEAL_ROOT_ID,
  KLARNA_ROOT_ID,
  PAYMENT_FORMS,
  STRIPE_ELEMENT_ROOT_ID,
} from 'constants/paymentSettingShared.constants'
import { Stripe, StripeElement } from 'shop/types/stripe'
import { useShopStores } from 'shop/hooks/use-store'

import { GooglePayExpressElement } from 'shared/components/purchase-methods/GooglePayExpressElement'
import { ApplePayExpressElement } from 'shared/components/purchase-methods/ApplePayExpressElement'

import AmEx from 'images/payment_methods/AmEx.svg'
import Card from './assets/paymethods/StripeCard.svg'

import './_autopay-handler-modal.scss'

interface AutopayHandlerModalProps {
  isOpen: boolean
  cancel?: boolean
  toggle: () => void
  paymentForm: string
  setStripeClient?: (client: Stripe) => void
  orderProvider?: string
  providers: Record<string, string>
  paymentInfo?: {
    redirectLink?: string
    clientSecret?: string
    payerFullName?: string
    payerCountryCode?: string
    payerEmail?: string
    setupIntentSecret?: string
  }
  getBillingDetails?: () => {
    billing_details?: {
      name: string
      email: string
    }
  }
  submit?: (client: Stripe, element: StripeElement) => void
  title: string
  message: string
  stripePubKey: string
  elopageConnectPubKey: string
  payOptions?: {
    amount: number
    currency: string
  }
}

export const AutopayHandlerModal: React.FC<AutopayHandlerModalProps> = ({
  isOpen = false,
  cancel = true,
  toggle,
  paymentForm,
  setStripeClient,
  orderProvider,
  providers,
  paymentInfo: { redirectLink, clientSecret, payerFullName, payerCountryCode, payerEmail, setupIntentSecret } = {},
  getBillingDetails,
  submit,
  title = I18n.t('react.shop.change_pricing_plan.with_payment.title'),
  message = I18n.t('react.shop.change_pricing_plan.with_payment.description'),
  payOptions,
  ...props
}) => {
  const { upgradeTicketsStore } = useShopStores()
  const showMessage =
    message &&
    paymentForm !== PAYMENT_FORMS.applePay &&
    paymentForm !== PAYMENT_FORMS.googlePay &&
    paymentForm !== PAYMENT_FORMS.klarna &&
    paymentForm !== PAYMENT_FORMS.ideal

  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false)
  const [stripeElementLoading, setStripeElementLoading] = useState<boolean>(false)
  const [stripeElementError, setStripeElementError] = useState<string>('')
  const [stripeCardLoading, setStripeCardLoading] = useState<boolean>(false)

  const stripeClient = useRef(null)
  const stripeElements = useRef(null)
  const stripeElement = useRef(null)

  const stripeCard = useRef(null)
  const stripeCardError = useRef(null)

  useEffect(() => {
    switch (paymentForm) {
      case PAYMENT_FORMS.sofort:
      case PAYMENT_FORMS.card:
      case PAYMENT_FORMS.applePay:
      case PAYMENT_FORMS.googlePay:
      case PAYMENT_FORMS.p24:
      case PAYMENT_FORMS.ideal:
      case PAYMENT_FORMS.klarna: {
        setIsSubmitDisabled(true)
        if (!isStripeInjected()) {
          injectStripeScript(handleStripeOnLoad)
        } else {
          handleStripeOnLoad()
        }
        break
      }

      default:
        break
    }
  }, [])

  const getElementId = ({ isKlarna, isIdeal }: { isKlarna: boolean; isIdeal: boolean }) => {
    switch (true) {
      case isIdeal:
        return IDEAL_ROOT_ID
      case isKlarna:
        return KLARNA_ROOT_ID
      default:
        return STRIPE_ELEMENT_ROOT_ID
    }
  }

  const handleStripeOnLoad = async () => {
    try {
      setStripeElementLoading(true)
      const isKlarna = paymentForm === PAYMENT_FORMS.klarna
      const isIdeal = paymentForm === PAYMENT_FORMS.ideal
      const provider = orderProvider
        ? `${snakeCaseToCamelCase(orderProvider)}PubKey`
        : getPubKey(paymentForm, providers)
      const pubKey = props[provider]

      if (pubKey) {
        stripeClient.current = await createStripeClient(pubKey)

        if (isKlarna) {
          stripeElements.current = createStripeElements(stripeClient.current, { clientSecret })
        } else if (isIdeal) {
          stripeElements.current = createStripeElements(stripeClient.current, { clientSecret })
        } else {
          stripeElements.current = createStripeElements(stripeClient.current)
        }

        setStripeClient && setStripeClient(stripeClient.current)

        if (paymentForm === PAYMENT_FORMS.p24) {
          stripeElement.current = createStripeP24(stripeElements.current)
        }

        if (paymentForm === PAYMENT_FORMS.ideal) {
          stripeElement.current = createStripeIdeal(stripeElements.current)
        }

        if (isKlarna) {
          stripeElement.current = createStripeKlarna(stripeElements.current, getKlarnaFormSettings())
        }

        if (stripeElement.current) {
          stripeElement.current.mount(`#${getElementId({ isKlarna, isIdeal })}`)

          stripeElement.current.on('ready', () => {
            setStripeElementLoading(false)
          })

          stripeElement.current.on('change', ({ complete, error }) => {
            if (error) {
              setStripeElementError(error.message)
            } else {
              setStripeElementError('')

              if (complete) {
                setIsSubmitDisabled(false)
              }
            }
          })

          stripeElement.current.on('loaderror', ({ error: { message } }) => {
            if (message) {
              setStripeElementError(message)
            }
          })
        }

        if (paymentForm === PAYMENT_FORMS.card) {
          // Create an instance of the card Element.
          stripeCard.current = createStripeCard(stripeElements.current)

          // Add an instance of the card Element into the `card-element` <div>.
          stripeCard.current.mount('#card-element')

          // Handle real-time validation errors from the card Element.
          stripeCardError.current = document.getElementById('card-errors')

          stripeCard.current.on('ready', () => {
            stripeCardError.current.classList.remove('lmask')
          })

          stripeCard.current.addEventListener('change', ({ complete, error }) => {
            if (error) {
              stripeCardError.current.textContent = error.message
              setIsSubmitDisabled(false)
            } else {
              stripeCardError.current.textContent = ''
              if (complete) {
                setIsSubmitDisabled(false)
              }
            }
          })
        }

        if (paymentForm === PAYMENT_FORMS.applePay || paymentForm === PAYMENT_FORMS.googlePay) {
          setIsSubmitDisabled(false)
        }
      }
    } catch (e) {}
  }

  const defaultSubmit = async () => {
    const billingDetails = {
      fullName: payerFullName,
      email: payerEmail,
    }

    switch (paymentForm) {
      case PAYMENT_FORMS.card: {
        setIsSubmitDisabled(true)
        setStripeCardLoading(true)

        if (setupIntentSecret && clientSecret) {
          handleAdditionalConfirmation(
            stripeClient.current,
            () => (window.location.href = redirectLink),
            () => {
              stripeCard.current.clear()
              setStripeCardLoading(false)
            },
            clientSecret,
            { stripeCard: stripeCard.current },
            billingDetails
          )
        } else {
          handleStripeCardTransaction(
            stripeClient.current,
            { stripeCard: stripeCard.current },
            clientSecret,
            billingDetails
          ).then((result) =>
            handleTransactionResponse(
              result,
              () => (window.location.href = redirectLink),
              () => {
                stripeCard.current.clear()
                setStripeCardLoading(false)
              }
            )
          )
        }
        break
      }

      case PAYMENT_FORMS.sofort: {
        handleSofortTransaction(stripeClient.current, redirectLink, clientSecret, {
          countryCode: payerCountryCode,
          billingDetails: getBillingDetails(),
        })
        break
      }

      case PAYMENT_FORMS.p24: {
        handleP24Transaction(stripeClient.current, stripeElement.current, redirectLink, clientSecret, {
          ...billingDetails,
          tosShowAndAccepted: false,
        })
        break
      }

      case PAYMENT_FORMS.ideal: {
        handleIdealTransaction(stripeClient.current, redirectLink, clientSecret, {
          ...billingDetails,
        })
        break
      }

      case PAYMENT_FORMS.klarna: {
        const { error } = await stripeElements.current.submit()
        if (error) {
          notify('error', error)
          break
        }

        window.location.href = redirectLink
        break
      }

      case PAYMENT_FORMS.googlePay:
      case PAYMENT_FORMS.applePay: {
        return {
          clientSecret,
          redirectLink,
        }
        break
      }

      default:
        break
    }
  }

  const handleSubmit = () => {
    if (submit) {
      submit(stripeClient.current, stripeElement.current)
    } else {
      return defaultSubmit()
    }
  }

  const renderPaymentSystem = () => {
    switch (paymentForm) {
      case PAYMENT_FORMS.card: {
        return (
          <div className='card-widget-container'>
            <div className='card-header'>
              <img className={`${PAYMENT_FORMS.card}-img`} alt={PAYMENT_FORMS.cardAmEx} src={AmEx} />
              <img className={`${PAYMENT_FORMS.card}-img`} alt={PAYMENT_FORMS.card} src={Card} />
            </div>
            <div className='card-widget'>
              <div id='card-element' />
              <div id='card-errors' className='lmask' />
            </div>
          </div>
        )
      }
      case PAYMENT_FORMS.applePay:
      case PAYMENT_FORMS.googlePay: {
        return (
          <div className='confirmation-modal__message'>
            {I18n.t('react.shared.payment_methods.digital_payment_confirmation_required')}
          </div>
        )
      }
      case PAYMENT_FORMS.klarna: {
        return (
          <Fragment>
            <div id={KLARNA_ROOT_ID} />
            {stripeElementLoading ? <div className='lmask' /> : null}
            {stripeElementError ? <div>{stripeElementError}</div> : null}
          </Fragment>
        )
      }

      case PAYMENT_FORMS.ideal: {
        return (
          <Fragment>
            <div id={IDEAL_ROOT_ID} />
            {stripeElementLoading ? <div className='lmask' /> : null}
            {stripeElementError ? <div>{stripeElementError}</div> : null}
          </Fragment>
        )
      }
      case PAYMENT_FORMS.p24:
      default: {
        return (
          <Fragment>
            <div id={STRIPE_ELEMENT_ROOT_ID} />
            {stripeElementLoading ? <div className='lmask' /> : null}
            {stripeElementError ? <div>{stripeElementError}</div> : null}
          </Fragment>
        )
      }
    }
  }

  const isGooglePay = paymentForm === PAYMENT_FORMS.googlePay
  const isApplePay = paymentForm === PAYMENT_FORMS.applePay

  const getCustomSubmitButtons = () => (
    <>
      {isGooglePay && (
        <GooglePayExpressElement
          show
          googleProvider={orderProvider}
          googlePubKey={props[`${snakeCaseToCamelCase(orderProvider)}PubKey`]}
          handleExpressClick={null}
          handleExpressSubmit={handleSubmit}
          handleExpressClose={() => {}}
          payOptions={{
            amount: payOptions?.amount,
            currency: payOptions?.currency,
          }}
        />
      )}
      {isApplePay && (
        <ApplePayExpressElement
          show
          appleProvider={orderProvider}
          applePubKey={props[`${snakeCaseToCamelCase(orderProvider)}PubKey`]}
          handleExpressClick={null}
          handleExpressSubmit={handleSubmit}
          handleExpressClose={() => {}}
          payOptions={{
            amount: payOptions?.amount,
            currency: payOptions?.currency,
          }}
        />
      )}
    </>
  )

  return (
    <EloModal
      onlySubmit
      flexButtons
      cancel={cancel}
      isOpen={isOpen}
      title={title}
      toggle={toggle}
      submit={handleSubmit}
      submitDisabled={isSubmitDisabled}
      submitButtonChild={I18n.t('react.shared.button.pay')}
      zIndex={!!Object.values(upgradeTicketsStore.selectedData).length ? 1100 : null}
      customSubmitButton={(isGooglePay || isApplePay) && getCustomSubmitButtons()}
    >
      <Fragment>
        {showMessage && (
          <Fragment>
            <div className='confirmation-modal__message'>{message}</div>
            <br />
          </Fragment>
        )}
        {renderPaymentSystem()}
        {stripeCardLoading && <LoadingMask />}
      </Fragment>
    </EloModal>
  )
}
